import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects";

const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ctor = typeof globalThis !== "undefined" && (globalThis.PrimitiveMeshNode || globalThis.PrimitiveMesh);
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

export const PRIMITIVE_MESH_PROPS = ["color", "geometryType", "geometryParameters", "castShadow", "receiveShadow"];

class PrimitiveMeshEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    PRIMITIVE_MESH_PROPS.forEach(prop => {
      if (prop === "geometryParameters") {
        Object.defineProperty(this, prop, {
          get: () => {
            const params = this._values.get(prop) || {};
            return new Proxy(params, {
              set: (target, key, value) => {
                target[key] = value;
                const next = { ...target };
                this._values.set(prop, next);
                const desc = this._originalDescriptors.get(prop);
                if (desc?.set) {
                  try {
                    desc.set.call(this.sourceObject, next);
                  } catch {
                    /* ignore */
                  }
                } else {
                  try {
                    this.sourceObject[prop] = next;
                  } catch {
                    /* ignore */
                  }
                }
                return true;
              }
            });
          },
          set: v => {
            const next = { ...(v || {}) };
            this._values.set(prop, next);
            const desc = this._originalDescriptors.get(prop);
            if (desc?.set) desc.set.call(this.sourceObject, next);
            else this.sourceObject[prop] = next;
          }
        });
        return;
      }

      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else if (prop === "color" && sourceObject.color?.set) {
            sourceObject.color.set(v);
            this._values.set(prop, sourceObject.color);
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (desc?.get) initial = desc.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });

    // Handle networkable separately
    Object.defineProperty(this, "networkable", {
      get: () => this._values.get("networkable"),
      set: v => {
        this._values.set("networkable", v);
        try {
          this.sourceObject.networkable = v;
        } catch {
          /* ignore */
        }
      }
    });

    // seed networkable
    let initialNetworkable;
    try {
      if (Object.prototype.hasOwnProperty.call(sourceObject, "networkable")) {
        initialNetworkable = sourceObject.networkable;
      }
    } catch {
      /* ignore */
    }
    this._values.set("networkable", initialNetworkable);
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

class PrimitiveMeshAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;
    const COMPONENT = "primitive-shape";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (pending[COMPONENT]) {
        const attr = el.getAttribute(COMPONENT) || {};
        // Filter out networkable from existing attributes
        const { networkable, ...cleanAttr } = attr;
        const current = { ...cleanAttr };
        Object.assign(current, pending[COMPONENT]);
        el.setAttribute(COMPONENT, current);
        delete pending[COMPONENT];
      }

      if (pending["shadow"]) {
        const current = { ...(el.getAttribute("shadow") || {}) };
        Object.assign(current, pending["shadow"]);
        el.setAttribute("shadow", current);
        delete pending["shadow"];
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      const name = e?.detail?.name;
      if (name === COMPONENT || name === "shadow") flush();
    });

    const setShapeProp = (k, v) => {
      // Skip networkable property as it's handled by the networked attribute separately
      if (k === "networkable") return;

      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        (pending[COMPONENT] ||= {})[k] = v;
      } else {
        // Filter out networkable from existing attributes to prevent it from being set on the component
        const { networkable, ...cleanAttr } = attr;
        const next = { ...cleanAttr, [k]: v };
        el.setAttribute(COMPONENT, next);
      }
    };
    const getShapeProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending[COMPONENT]?.[k];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[k]) ?? pending[COMPONENT]?.[k];
    };

    const setShadow = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute("shadow");
      if (!attr) {
        (pending["shadow"] ||= {})[k] = !!v;
      } else {
        const next = { ...attr, [k]: !!v };
        el.setAttribute("shadow", next);
      }
    };
    const getShadow = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending["shadow"]?.[k];
      const attr = el.getAttribute("shadow");
      return (attr && attr[k]) ?? pending["shadow"]?.[k];
    };

    // Bind API surface
    Object.defineProperties(this, {
      geometryType: { get: () => getShapeProp("geometryType"), set: v => setShapeProp("geometryType", v) },
      geometryParameters: {
        get: () => {
          const params = getShapeProp("geometryParameters") || {};
          return new Proxy(params, {
            set: (t, k, v) => {
              t[k] = v;
              setShapeProp("geometryParameters", { ...t });
              return true;
            }
          });
        },
        set(p) {
          setShapeProp("geometryParameters", { ...(p || {}) });
        }
      },
      color: {
        get: () => getShapeProp("color"),
        set(v) {
          setShapeProp("color", v);
        }
      },
      castShadow: {
        get: () => getShadow("cast"),
        set(v) {
          setShadow("cast", v);
        }
      },
      receiveShadow: {
        get: () => getShadow("receive"),
        set(v) {
          setShadow("receive", v);
        }
      },
      networkable: {
        get: () => !!this.sourceObject?.el?.hasAttribute("networked"),
        set: v => {
          const el = this.sourceObject?.el;
          if (!el) return;
          if (v) {
            if (!el.hasAttribute("networked")) {
              el.setAttribute("networked", { template: "#interactable-primitive-shape" });
            }
          } else {
            el.removeAttribute("networked");
          }
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of PRIMITIVE_MESH_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new PrimitiveMeshAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new PrimitiveMeshEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class PrimitiveMeshElement extends ScriptableObject {
  static scriptableType = "primitive-shape";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("primitive-shape", {
        color: initial.color ?? "#AAA",
        geometryType: initial.geometryType ?? "Box",
        geometryParameters: initial.geometryParameters ?? {}
      });

      if (initial.networkable) {
        el.setAttribute("networked", { template: "#interactable-primitive-shape" });
      }

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real PrimitiveMeshNode and upgrade to scriptable
    const node = createPrimitiveMeshElementEditor(initial);
    return node;
  }

  copy(source, recursive = true, { preserveNetworked = true } = {}) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (prop === "color" && val?.isColor) {
        this.color = val.clone(); // editor/THREE path
      } else if (prop === "geometryParameters" && val) {
        this.geometryParameters = { ...val }; // plain object
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone(); // vectors, etc.
      } else {
        this[prop] = val;
      }
    };

    for (const p of PRIMITIVE_MESH_PROPS) {
      take(p, source[p]);
    }

    // Handle networkable separately since it's not a primitive-shape component property
    if (preserveNetworked) {
      take("networkable", source.networkable);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true, { preserveNetworked = true } = {}) {
    const init = {};

    for (const p of PRIMITIVE_MESH_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      if (p === "color" && v?.isColor) init[p] = v.clone();
      else if (p === "geometryParameters" && v) init[p] = { ...v };
      else if (Array.isArray(v)) init[p] = v.slice();
      else if (v && typeof v.clone === "function") init[p] = v.clone();
      else init[p] = v;
    }

    // Handle networkable separately since it's not a primitive-shape component property
    if (preserveNetworked) {
      const v = this.networkable;
      if (v !== undefined) {
        init.networkable = v;
      }
    }

    // Prefer the class factory if present (plays nice if constructor/adopt is customized)
    const Ctor =
      this.constructor && typeof this.constructor.create === "function" ? this.constructor : PrimitiveMeshElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new PrimitiveMeshElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive, { preserveNetworked });
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade an editor PrimitiveMeshNode to scriptable and attach adapter + proxies
function makeEditorPrimitiveMeshNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ctor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType)
      Object.defineProperty(node, "scriptableType", { value: PrimitiveMeshElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = PrimitiveMeshElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", {
      value: new PrimitiveMeshEditorAdapter(node, descriptors),
      configurable: true
    });
  } catch (_) {
    node._adapter = new PrimitiveMeshEditorAdapter(node, descriptors);
  }

  for (const prop of PRIMITIVE_MESH_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  try {
    if (node.clone !== PrimitiveMeshElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: PrimitiveMeshElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch (_) {
    node.clone = PrimitiveMeshElement.prototype.clone;
  }

  return node;
}

// Editor factory for PrimitiveMesh (editor/plain Three)
function createPrimitiveMeshElementEditor(initial = {}) {
  const Ctor = globalThis.PrimitiveMeshNode || globalThis.PrimitiveMesh;
  if (typeof Ctor !== "function") {
    throw new Error("PrimitiveMeshElementEditor: PrimitiveMeshNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`PrimitiveMeshElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorPrimitiveMeshNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

// If you create objects via adopt(el) (constructor doesn’t run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
PRIMITIVE_MESH_PROPS.forEach(prop => {
  Object.defineProperty(PrimitiveMeshElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

// Handle networkable separately to prevent it from being treated as a primitive-shape component property
Object.defineProperty(PrimitiveMeshElement.prototype, "networkable", {
  get() {
    return ensureAdapter(this)?.networkable;
  },
  set(v) {
    const a = ensureAdapter(this);
    if (a) a.networkable = v;
  },
  enumerable: true,
  configurable: true
});

ScriptableObjects.registerType("primitive-shape", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType;

  if (type !== PrimitiveMeshElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) {
    return PrimitiveMeshElement.adopt(source);
  }

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorPrimitiveMeshNodeScriptable(source);

  return source;
});
